import React from 'react';

import pick from 'lodash/pick';

import {
    CustomComponent,
    Store,
    PLACEMENT,
    CustomizationSubscription,
    Targets,
    Events,
    EventSubscription,
    MAX_EVENTS,
    TargetsWidgetType,
    type CustomizationAdditionalFilters,
    CustomizationAdditionalFiltersNames,
    type CustomizationSearchFilters,
    CustomizationSearchFiltersNames,
} from '../types';

const store: Store = {
    customComponents: {},
    observers: {},
    onSubscribers: [],
    initialized: false,
    disabledComponents: window.lumapps ? window.lumapps.disabledComponents : {},
    disabledObservers: {},
    widgetAdditionalFilters: window.lumapps ? window.lumapps.widgetAdditionalFilters : {},
    widgetAdditionalFiltersObservers: {},
    searchAdditionalFilters: window.lumapps ? window.lumapps.searchAdditionalFilters : {},
    searchAdditionalFiltersObservers: [],
    textsOverrides: {},
    functionalOverrides: {},
    events: [],
};

/**
 * Unfortunately, when the legacy application creates its bundles, it separates
 * the Angular JS files in one bundle and the React files in another. Since this file
 * is used on both sides, this Singleton gets created TWICE (yes seriously), since the
 * file is two times in our bundles, once on the legacy side and once on the migrated code.
 *
 * Therefore, stores have different values, depending where they are executed. In order to have
 * only just one store, we use a global variable that will allow us to really have only one store.
 *
 * Fortunately, this came in helpful since we needed to have a way to debug and evaluate the customizations
 * that were added to the store, so this is the perfect excuse to expose the store.
 */
if (window.lumapps) {
    window.lumapps.store = store;
}

const getStore = (): Store => window.lumapps?.store || store;

/**
 * Returns true if the store was initialized
 */
const wasStoreInitialized = () => getStore().initialized;

/**
 * Sets the customizations store as initialized
 */
const setStoreAsInitialized = () => {
    getStore().initialized = true;
};

/**
 * Retrieves the customization available for the provided component and placement
 * @param target - string
 * @param placement - PLACEMENT
 */
const getCustomComponent = ({ target, placement }: { target: string; placement: PLACEMENT }) => {
    const customizationStore = getStore();

    return customizationStore.customComponents[target] ? customizationStore.customComponents[target][placement] : null;
};

/**
 * Returns true if there is a customization for the provided component. If placement is passed in, it will
 * return true if there is a customization for the provided component and placement as well.
 * @param target - string
 * @param placement - PLACEMENT
 */
const doesCustomizationExist = ({ target, placement }: { target: string; placement?: PLACEMENT }) => {
    const customizationStore = getStore();

    if (!placement) {
        return Boolean(customizationStore.customComponents[target]);
    }

    return customizationStore.customComponents[target]
        ? Boolean(customizationStore.customComponents[target][placement])
        : false;
};

/**
 * Adds a customization to the store and if there is already an observer for that
 * customization, it will trigger it allowing the update to occur.
 * @param customization - CustomComponen
 */
const setCustomComponent = (customization: CustomComponent) => {
    const customizationStore = getStore();
    const { target, placement, targets = [] } = customization;

    const customizableTargets = targets.length > 0 ? targets : [target];

    customizableTargets.forEach((customizableTarget) => {
        if (!customizationStore.customComponents[customizableTarget]) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            customizationStore.customComponents[customizableTarget] = {
                [placement]: customization,
            };
        } else {
            customizationStore.customComponents[customizableTarget][placement] = customization;
        }

        if (!customizationStore.observers[customizableTarget]) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            customizationStore.observers[customizableTarget] = {
                [placement]: null,
            };
        }

        if (customizationStore.observers[customizableTarget][placement]) {
            customizationStore.observers[customizableTarget][placement].forEach((callback: any) => {
                try {
                    callback(customization);
                } catch (excp) {
                    // eslint-disable-next-line no-console
                    console.warn(excp);
                }
            });
        }
    });
};

/**
 * Subscribe to a customization change
 * @param component - string
 * @param placement - PLACEMENT
 * @param callback - action to be executed when there is an update.
 */
const subscribeToCustomization = ({ target, placement, callback }: CustomizationSubscription) => {
    const customizationStore = getStore();

    if (!customizationStore.observers[target]) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        customizationStore.observers[target] = {};
    }

    if (!customizationStore.observers[target][placement]) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        customizationStore.observers[target][placement] = [];
    }

    customizationStore.observers[target][placement].push(callback);
};

/** Adds event to the store */
const subscribeTo = ({ callback, event }: EventSubscription) => {
    const customizationStore = getStore();

    // check that an event id has been provided with the callback
    if (!event) {
        return;
    }

    customizationStore.onSubscribers.push({ event, callback });
};

// execute callback if summoned subscriber is found
const notifySubscribers = (props: any, event: Events) => {
    const customizationStore = getStore();

    if (customizationStore.events.length === MAX_EVENTS) {
        customizationStore.events.shift();
    }

    customizationStore.events.push({
        eventType: event,
        data: props,
        timestamp: new Date(),
    });

    // check that there is at least 1 subscriber before doing anything
    if (customizationStore.onSubscribers.length === 0) {
        return;
    }
    // filter known events to have only the one required and then execute the callback
    const subscribers = customizationStore?.onSubscribers.filter((v) => v.event === event);

    if (subscribers && subscribers.length > 0) {
        subscribers.forEach((e) => {
            try {
                e.callback(props);
            } catch (excp) {
                // eslint-disable-next-line no-console
                console.warn(excp);
            }
        });
    }
};

const getLatestEvents = (eventType?: string) => {
    const customizationStore = getStore();

    if (eventType) {
        return customizationStore.events.filter((event) => event.eventType === eventType);
    }

    return customizationStore.events;
};

/** Returns whether a component is disabled or not */
const isComponentDisabled = (target: Targets): boolean => {
    const { disabledComponents } = window.lumapps.store || getStore() || {};

    return disabledComponents ? disabledComponents[target as string] : false;
};

const subscribeToDisable = (target: Targets, callback: any) => {
    const customizationStore = getStore();

    if (!customizationStore.disabledObservers[target]) {
        customizationStore.disabledObservers[target] = [];
    }

    customizationStore.disabledObservers[target].push(callback);
};

const isWidgetFiltersCustomizable = (widgetType: unknown): widgetType is TargetsWidgetType =>
    Object.values(TargetsWidgetType).includes(widgetType as TargetsWidgetType);

const subscribeToWidgetFilterAdd = (targetWidgetType: TargetsWidgetType, callback: any) => {
    const customizationStore = getStore();

    // Only supported widgets can have their filters modified.
    if (!isWidgetFiltersCustomizable(targetWidgetType)) {
        return;
    }

    if (!customizationStore.widgetAdditionalFiltersObservers?.[targetWidgetType]) {
        customizationStore.widgetAdditionalFiltersObservers[targetWidgetType] = [];
    }

    customizationStore.widgetAdditionalFiltersObservers[targetWidgetType].push(callback);
};

/** Add filters to all widgets matching the target widget type */
const addWidgetFilters = (targetWidgetType: TargetsWidgetType, filters: CustomizationAdditionalFilters) => {
    const customizationStore = window.lumapps.store || getStore() || {};

    // Only supported widgets can have their filters modified.
    if (!isWidgetFiltersCustomizable(targetWidgetType)) {
        return;
    }

    if (!customizationStore.widgetAdditionalFilters) {
        customizationStore.widgetAdditionalFilters = {};
    }

    // Passed filters are filtered to pick only supported filters.
    const filteredFilters = pick(filters, Object.values(CustomizationAdditionalFiltersNames));

    customizationStore.widgetAdditionalFilters[targetWidgetType] = filteredFilters;

    if (customizationStore.widgetAdditionalFiltersObservers[targetWidgetType]) {
        customizationStore.widgetAdditionalFiltersObservers[targetWidgetType].forEach((callback: any) =>
            callback(filteredFilters),
        );
    }
};

const disableComponent = (target: Targets) => {
    const customizationStore = window.lumapps.store || getStore() || {};

    if (!customizationStore.disabledComponents) {
        customizationStore.disabledComponents = {};
    }

    customizationStore.disabledComponents[target] = true;

    if (customizationStore.disabledObservers[target]) {
        customizationStore.disabledObservers[target].forEach((callback: any) => callback());
    }
};

const useAdditionalFilters = (targetWidgetType: TargetsWidgetType) => {
    const { widgetAdditionalFilters } = window.lumapps.store || getStore() || {};

    const [filters, setFilters] = React.useState<CustomizationAdditionalFilters>(
        widgetAdditionalFilters?.[targetWidgetType],
    );

    React.useEffect(() => {
        subscribeToWidgetFilterAdd(targetWidgetType, setFilters);
    }, [targetWidgetType]);

    // Only supported widgets can have their filters modified.
    if (!isWidgetFiltersCustomizable(targetWidgetType)) {
        return undefined;
    }

    return filters;
};

const useIsComponentDisabled = (target: Targets): boolean => {
    const { disabledComponents } = window.lumapps.store || getStore() || {};
    const isDisabled = disabledComponents ? disabledComponents[target as string] : false;
    const [, setDisabled] = React.useState(isDisabled);

    React.useEffect(() => {
        subscribeToDisable(target, setDisabled);
    }, [target]);

    return isDisabled;
};

/** Returns a text override for a given target */
const getTextOverride = (target: Targets): Record<string, string> => {
    const { textsOverrides } = window.lumapps || getStore() || {};

    return textsOverrides ? textsOverrides[target as string] : {};
};

/** Returns a functional override for a given target */
const getFunctionalOverride = (target: Targets): Record<string, any> => {
    const { functionalOverrides } = window.lumapps || getStore() || {};

    return functionalOverrides ? functionalOverrides[target as string] : {};
};

const setFunctionalOverride = (target: Targets, override: Record<string, any>) => {
    const customizationStore = window.lumapps || getStore() || {};

    if (!customizationStore.functionalOverrides) {
        customizationStore.functionalOverrides = {};
    }

    customizationStore.functionalOverrides[target] = override;
};

const reset = () => {
    Object.assign(window.lumapps.store, {
        customComponents: {},
        observers: {},
        subscribers: [],
        disabledObservers: {},
        disableComponent: {},
        widgetAdditionalFiltersObservers: {},
        widgetAdditionalFilters: {},
        textsOverrides: {},
        functionalOverrides: {},
    });
};

/** Add filters to all search queries */
const addSearchFilters = (filters: CustomizationSearchFilters) => {
    const customizationStore = getStore();

    if (!customizationStore.searchAdditionalFilters) {
        customizationStore.searchAdditionalFilters = {};
    }

    // Passed filters are filtered to pick only supported filters.
    const filteredFilters = pick(filters, Object.values(CustomizationSearchFiltersNames));

    customizationStore.searchAdditionalFilters = filteredFilters;

    if (customizationStore.searchAdditionalFiltersObservers) {
        customizationStore.searchAdditionalFiltersObservers.forEach(
            (callback: (filters: CustomizationSearchFilters) => void) => callback(filteredFilters),
        );
    }
};

/** Get current search filters */
const getSearchAdditionalFilters = (): CustomizationSearchFilters | undefined => {
    const customizationStore = getStore();
    return customizationStore.searchAdditionalFilters;
};

const subscribeToSearchFilterAdd = (callback: (filters: CustomizationSearchFilters) => void) => {
    const customizationStore = getStore();

    if (!customizationStore.searchAdditionalFiltersObservers) {
        customizationStore.searchAdditionalFiltersObservers = [];
    }

    customizationStore.searchAdditionalFiltersObservers.push(callback);

    // Return unsubscribe function
    return () => {
        if (customizationStore.searchAdditionalFiltersObservers) {
            customizationStore.searchAdditionalFiltersObservers =
                customizationStore.searchAdditionalFiltersObservers.filter(
                    (cb: (filters: CustomizationSearchFilters) => void) => cb !== callback,
                );
        }
    };
};

const useSearchAdditionalFilters = (): CustomizationSearchFilters | undefined => {
    const customizationStore = getStore();
    const { searchAdditionalFilters } = customizationStore;

    const [filters, setFilters] = React.useState<CustomizationSearchFilters>(searchAdditionalFilters || {});

    React.useEffect(() => {
        const unsubscribe = subscribeToSearchFilterAdd(setFilters);
        return unsubscribe;
    }, []);

    return filters;
};

export default {
    wasStoreInitialized,
    subscribeToCustomization,
    setCustomComponent,
    doesCustomizationExist,
    getCustomComponent,
    setStoreAsInitialized,
    isComponentDisabled,
    reset,
    getTextOverride,
    disableComponent,
    addWidgetFilters,
    useAdditionalFilters,
    useIsComponentDisabled,
    getFunctionalOverride,
    setFunctionalOverride,
    subscribeTo,
    notifySubscribers,
    getLatestEvents,
    addSearchFilters,
    getSearchAdditionalFilters,
    subscribeToSearchFilterAdd,
    useSearchAdditionalFilters,
};
